import { useEffect, useState } from 'react';
import type { NowPlayingPayload } from '../types';

const POLL_INTERVAL_MS = 30_000;

function isNowPlayingPayload(value: unknown): value is NowPlayingPayload {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Partial<NowPlayingPayload>;
  if (payload.isPlaying !== true || !payload.track) return false;
  return typeof payload.track.title === 'string'
    && typeof payload.track.artist === 'string'
    && typeof payload.track.url === 'string'
    && payload.attribution === 'Last.fm';
}

export function NowPlaying() {
  const [nowPlaying, setNowPlaying] = useState<NowPlayingPayload | null>(null);

  useEffect(() => {
    let stopped = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;

    const poll = async () => {
      controller = new AbortController();
      try {
        const response = await fetch('/api/now-playing', {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        const payload: unknown = response.ok ? await response.json() : null;
        if (!stopped) setNowPlaying(isNowPlayingPayload(payload) ? payload : null);
      } catch (error) {
        if (!stopped && (!(error instanceof Error) || error.name !== 'AbortError')) setNowPlaying(null);
      } finally {
        if (!stopped && document.visibilityState !== 'hidden') timeout = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (timeout) clearTimeout(timeout);
        controller?.abort();
        return;
      }
      if (timeout) clearTimeout(timeout);
      void poll();
    };

    void poll();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stopped = true;
      if (timeout) clearTimeout(timeout);
      controller?.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (!nowPlaying?.track) return null;

  return (
    <aside className="now-playing" aria-label="Currently playing" aria-live="polite">
      <a href={nowPlaying.track.url} target="_blank" rel="noreferrer">
        <span className="now-playing-meter" aria-hidden="true"><i /><i /><i /><i /></span>
        <span className="now-playing-label">Now playing</span>
        <span className="now-playing-track"><strong>{nowPlaying.track.title}</strong><span>{nowPlaying.track.artist}</span></span>
        {nowPlaying.track.album && <span className="now-playing-album">{nowPlaying.track.album}</span>}
        <span className="now-playing-source">Last.fm <span aria-hidden="true">↗</span></span>
      </a>
    </aside>
  );
}
